import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Vector3 } from 'three'

import {
  ControlOptions,
  ModelControlsInterface,
} from '@src/components/adgroup-three-fiber/objects/hover-hitbox'

import { Unit } from '@src/utilities/adgroup-utilities/types'

import { setFullScreen } from '@store/actionSlices/appConfig'
import { RouteKey, Template } from '@store/constants'
import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import {
  ArrowSvg,
  FilterSvg,
  FullScreenSvg,
} from '@components/adgroup-svg/react'
import Model, { ModelRefProps } from '@components/adgroup-three-fiber/model'
import { ModelLoader } from '@components/adgroup-three-fiber/types'
import ContainerV1 from '@components/container/container'
import ContainerV2 from '@components/container/v2/container'
import DataHandler from '@components/data-handler'
import { Status } from '@components/data-handler/data-handler'
import FilterV1 from '@components/filter'
import FilterV2 from '@components/filter/v2'
import ZoneDropdown from '@components/zone-dropdown/v2'

import ThreeDSkeleton from '@pages/3d/3d-skeleton'

import {
  Level,
  selectFromResult as selectFromBuildingResult,
  useGetBuildingQuery,
} from '@api/building'
import { MappingBlockCollection } from '@api/interactive-plan'
import {
  Model as ModelApiInterface,
  ModelControlAction,
  ModelControl as ModelControlsApiInterface,
  Scene as SceneInterface,
  selectFromSceneListResult,
  useGetScenesQuery,
} from '@api/showcase-3d'

import filterUnit from '@utilities/adgroup-utilities/filter-unit'
import DeviceDetect from '@utilities/adgroup-utilities/hooks/device-detect'
import useStateRef from '@utilities/use-state-ref'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import HighlightedLevelInfo from './highlighted-level-info'

const ENABLE_PERFORMANCE_STATS =
  process.env.REACT_APP_ENABLE_3D_PERFORMANCE_STATS

interface ModelViewInterface extends Omit<ModelApiInterface, 'loader'> {
  loader: ModelLoader
}

interface ThreeDViewProps {
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  showPrice: boolean
  fullscreen: boolean
  template: Template
  storeBlocks: MappingBlockCollection
}

const PROPERTY_LABEL_PREFIX = 'Level'

function ADThreeDView({
  projectIdentity,
  session,
  showPrice,
  fullscreen,
  template,
  storeBlocks,
}: ThreeDViewProps) {
  const dispatch = useDispatch()

  const { isDesktop } = DeviceDetect()

  const [detectChange, setDetectChange] = useState(false)

  const [activeScene, setActiveScene] = useState<SceneInterface>()

  const [models, setModels] = useState<ModelViewInterface[]>([])
  const [activeModel, setActiveModel] = useState<string>(
    session?.threeDView?.activeModel || ''
  )
  const activeModelData = useMemo(
    () => models?.find((item) => item.id === activeModel),
    [models, activeModel]
  )

  const [modelControl, setModelControl] = useState<ModelControlsInterface>()

  const [isModelLoaded, setIsModelLoaded] = useState(false)

  const [levels, setLevels] = useState<Level[]>([])
  const [blocks, setBLocks] = useState<string[]>([])

  const unitFilter = session?.building?.unitFilter
  const activeBlock = session?.building?.activeBlock || ''

  const [highlightedTarget, setHighlightedTarget] = useState('')

  const [isFilterOpen, toggleFilter] = useState(false)

  const [position, setPosition] = useState<Vector3>(new Vector3())

  const [highlightedLevel, setHighlightedLevel] = useState('')
  const [propertyCount, setPropertyCount] = useState(0)

  const modelRef = useRef<ModelRefProps>(null)

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const scenesQueryResult = useGetScenesQuery(
    {
      projectIdOrName: projectIdentity.projectName,
      include: 'models,models.control',
      limit: 1,
    },
    { selectFromResult: selectFromSceneListResult }
  )

  const buildingQueryResult = useGetBuildingQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult: selectFromBuildingResult }
  )
  const { blockOrders } = buildingQueryResult

  const findRelevantLevel = useCallback(
    (levelName: string) => levels.find((res) => levelName === res.level),
    [levels]
  )

  const canUnitFilterByBlock = useCallback(
    (unit: Unit) => activeBlock && unit.blockId && unit.blockId !== activeBlock,
    [activeBlock]
  )

  const blockOrdersRef = useStateRef(blockOrders)
  const levelsRef = useStateRef(levels)

  const canRenderModel = models.length > 0 && !!modelControl

  const validateActionTargetId = useCallback<
    (blockId?: string, levelId?: string) => boolean
  >((blockId, levelId) => {
    const currentBlockOrders = blockOrdersRef.current
    const currentLevels = levelsRef.current

    let isValid = true

    if (currentBlockOrders?.length > 1 && !blockId) {
      isValid = false
    }
    if (blockId && !currentBlockOrders?.includes(blockId)) {
      isValid = false
    }
    if (!levelId || !currentLevels?.some((level) => level.level === levelId)) {
      isValid = false
    }

    return isValid
  }, [])

  const parseActionTargetId = useCallback(
    (actionTarget: string) => {
      const separator = '-'
      const splitted = actionTarget.trim().split(separator)
      const blockId = splitted.length > 1 ? splitted[0]?.trim() : undefined
      const levelId = splitted[splitted.length - 1]?.trim()
      const isValid = validateActionTargetId(blockId, levelId)

      return {
        levelId,
        blockId,
        isValid,
      }
    },
    [validateActionTargetId]
  )

  const levelDataByBlock = useCallback(
    (foundLevel: Level) =>
      blocks.length > 1
        ? foundLevel.data.filter((unit) => unit.blockId === activeBlock)
        : foundLevel.data,
    [activeBlock]
  )

  const getHighlightControlLabel = useCallback(
    (actionTarget: string) => {
      const controls = Object.values(modelControl ?? {})
      const matchControl = controls.find(
        (control) => control?.actionTarget === actionTarget
      )
      return matchControl?.label || null
    },
    [modelControl]
  )

  const getLevelStatus = useCallback(
    (actionTarget?: string) => {
      if (!actionTarget || !unitFilter) {
        return false
      }
      const { apply } = unitFilter
      const { levelId, blockId, isValid } = parseActionTargetId(actionTarget)

      if (!isValid) {
        return false
      }

      const foundLevel = findRelevantLevel(levelId)

      let levelActive = false

      if (!apply || !foundLevel) {
        return levelActive
      }

      levelDataByBlock(foundLevel).forEach((unit) => {
        if (canUnitFilterByBlock(unit)) {
          return
        }
        if (blockId && blockId !== unit.blockId) {
          return
        }

        if (!levelActive) {
          levelActive = unitFilter?.apply
            ? filterUnit(
                unit,
                unitFilter,
                showPrice,
                projectIdentity?.statusLabels?.available
              )
            : true
        }
      })

      return levelActive
    },
    [
      unitFilter,
      findRelevantLevel,
      levelDataByBlock,
      canUnitFilterByBlock,
      showPrice,
      parseActionTargetId,
    ]
  )

  const updateVisibleModel = (targetId: string) => {
    setModels((prevModels) =>
      prevModels.map((model) => ({
        ...model,
        visible: model.id === targetId,
      }))
    )
  }

  const handleBackButton = () => {
    const foundActiveModel = models.find((model) => model.id === activeModel)

    if (foundActiveModel?.prev) {
      const targetId = foundActiveModel.prev

      modelRef?.current?.startStaticLoader(targetId)
      setActiveModel(targetId)
      updateVisibleModel(targetId)

      firebaseControlQuery.update({
        'threeDView.activeModel': foundActiveModel.prev,
        'threeDView.canAnimate': false,
      })
    }
  }

  const handleOnFullScreen = () => {
    dispatch(setFullScreen(!fullscreen))
  }

  const changeScene = useCallback(
    (targetIdArg: string) => {
      const targetId = targetIdArg?.trim()
      const isValid = models.some((model) => model.id === targetId)
      if (!isValid) {
        return
      }
      modelRef?.current?.startStaticLoader(targetId)
      setActiveModel(targetId)
      updateVisibleModel(targetId)
    },
    [updateVisibleModel, models]
  )

  const activeBlockRef = useStateRef(activeBlock)

  const redirectToBuilding = useCallback(
    (actionTarget: string) => {
      const { levelId, blockId, isValid } = parseActionTargetId(actionTarget)
      if (!isValid) {
        return
      }
      const firebasePayload: Record<string, string> = {
        activeRoute: RouteKey.BUILDING,
        [`building.activeLevel`]: levelId || '',
      }
      if (blockId && activeBlockRef.current !== blockId) {
        firebasePayload['building.activeBlock'] = blockId
      }
      firebaseControlQuery.update(firebasePayload)
    },
    [parseActionTargetId]
  )

  const canAction = useCallback(
    (control: Pick<ControlOptions, 'action' | 'actionTarget'>) =>
      !!(control?.action && control?.actionTarget),
    []
  )

  const handleModelOnClick = (obj: ControlOptions) => {
    if (obj?.action === ModelControlAction.ChangeScene && obj?.actionTarget) {
      changeScene(obj.actionTarget)
    }
    if (
      obj?.action === ModelControlAction.RedirectBuilding &&
      obj?.actionTarget
    ) {
      redirectToBuilding(obj.actionTarget)
    }
  }

  const handleModelControlOnFilter = (
    modelControls: ModelControlsInterface
  ) => {
    const modelControlMapped: ModelControlsInterface = {}

    Object.keys(modelControls).forEach((key) => {
      if (modelControls[key].id) {
        modelControlMapped[key] = {
          ...modelControls[key],
          hoverActive: canAction(modelControls[key])
            ? getLevelStatus(modelControls[key].actionTarget) || false
            : modelControls[key]?.hoverActive,
        }
      }
    })

    return modelControlMapped
  }

  const setModelControlOnClick = (
    modelControls: ModelControlsApiInterface[]
  ) => {
    const modelControlsMapped: ModelControlsInterface = {}

    modelControls.forEach((control) => {
      modelControlsMapped[control.name] = {
        id: control.actionTarget,
        label: control.label,
        actionTarget: control.actionTarget,
        action: control.action,
        hoverColor: control.hoverColor,
        hoverActive: control.hoverActive,
        position: control.position,
        rotation: control.rotation,
        scale: control.scale,
        onClick: (arg) => handleModelOnClick(arg),
      }
    })

    return modelControlsMapped
  }

  const mapSourceModelsToView = (
    models: ModelApiInterface[]
  ): ModelViewInterface[] =>
    models.map((item) => ({
      ...item,
      id: item.name,
      visible: activeModel ? item.name === activeModel : item.visible,
      loader: {
        icon: item.loader.icon,
        bg: item.loader.bg,
        bar: {
          color: item.loader.color,
        },
      },
    }))

  const blockKeys = React.useMemo(
    () =>
      blockOrders.length > 1 ? blockOrders : Object.keys(storeBlocks || {}),
    [blockOrders, storeBlocks]
  )

  const blockOptions = React.useMemo(() => {
    if (blockOrders.length) {
      return blockOrders.map((item) => ({
        label: item,
        value: item,
      }))
    }

    return Object.keys(storeBlocks || {})?.map((item) => ({
      label: item,
      value: item,
    }))
  }, [blockOrders, storeBlocks])

  const [cameraIsAnimating, setCameraIsAnimating] = useState(false)

  const handleGetPosition = useCallback(
    (position: Vector3) => {
      if (cameraIsAnimating) {
        return
      }
      setPosition(position)
    },
    [cameraIsAnimating]
  )

  const onChangeZone = useCallback((item: string) => {
    firebaseControlQuery.update({
      [`building.activeBlock`]: item,
    })
    setHighlightedTarget('')
  }, [])

  const resetHighlightedLevelInfo = (): void => {
    setHighlightedLevel('')
    setPropertyCount(0)
  }

  const setHighlightedLevelInfo = useCallback(
    (actionTarget: string) => {
      if (!activeModelData?.showLevelInfoOnHover) {
        return
      }

      if (!actionTarget) {
        resetHighlightedLevelInfo()
        return
      }

      const { levelId, blockId } = parseActionTargetId(actionTarget)
      if (!levelId) {
        return
      }

      const customLabel = getHighlightControlLabel(actionTarget)
      setHighlightedLevel(`${customLabel || PROPERTY_LABEL_PREFIX} ${levelId}`)

      const foundLevel = findRelevantLevel(levelId)
      if (!foundLevel) {
        return
      }

      let units = blockId
        ? foundLevel.data.filter((unit) => unit.blockId === blockId)
        : foundLevel.data

      if (unitFilter?.apply) {
        units = units.filter((unit: Unit) =>
          filterUnit(
            unit,
            unitFilter,
            showPrice,
            projectIdentity?.statusLabels?.available
          )
        )
      }

      setPropertyCount(units.length)
    },
    [
      unitFilter,
      findRelevantLevel,
      showPrice,
      parseActionTargetId,
      modelControl,
    ]
  )

  const loaderInitialized = useRef(false)
  useEffect(() => {
    if (loaderInitialized.current || !canRenderModel || !activeModel) {
      return
    }
    modelRef?.current?.startStaticLoader(activeModel)
    loaderInitialized.current = true
  }, [canRenderModel])

  useEffect(() => {
    setHighlightedTarget('')
  }, [unitFilter?.apply])

  useEffect(() => {
    if (!blockOptions?.length) {
      return
    }
    let block = ''

    if (blockOptions.length > 1) {
      const isValidFirebaseActiveBlock = blockOptions?.some(
        (blockOption) => blockOption.value === activeBlock
      )
      block =
        (isValidFirebaseActiveBlock ? activeBlock : blockOptions[0]?.value) ||
        ''
    }

    firebaseControlQuery.update({
      [`building.activeBlock`]: block,
    })
  }, [blockOptions?.length])

  useEffect(() => {
    if (unitFilter && modelControl) {
      setModelControl(handleModelControlOnFilter(modelControl))
    }
  }, [unitFilter])

  useEffect(() => {
    firebaseControlQuery.update({
      'threeDView.highLightTarget': highlightedTarget,
    })
  }, [highlightedTarget])

  useEffect(() => {
    firebaseControlQuery.update({
      'threeDView.canAnimate': detectChange,
    })
  }, [detectChange])

  useEffect(() => {
    firebaseControlQuery.update({
      'threeDView.position': [position.x, position.y, position.z],
    })
  }, [position])

  useEffect(() => {
    firebaseControlQuery.update({
      'threeDView.modelLoaded': isModelLoaded,
    })
  }, [isModelLoaded])

  useEffect(() => {
    firebaseControlQuery.update({
      'threeDView.activeModel': activeModel,
      'threeDView.canAnimate': false,
    })
  }, [activeModel])

  useEffect(() => {
    firebaseControlQuery.update({
      'threeDView.activeScene': activeScene?.name || '',
      'threeDView.canAnimate': false,
    })
  }, [activeScene?.name])

  useEffect(
    () => () => {
      firebaseControlQuery.update({
        'threeDView.canAnimate': false,
        'threeDView.modelLoaded': false,
        'threeDView.position': [],
      })
    },
    []
  )

  useEffect(() => {
    if (!models?.length) {
      return
    }

    const currentModel = models.find((model) => model.visible)
    if (!currentModel) {
      return
    }

    setActiveModel(currentModel.id)
    setModelControl(setModelControlOnClick(currentModel.modelControl))
  }, [models])

  useEffect(() => {
    if (!activeScene?.models?.length) {
      return
    }

    setModels(mapSourceModelsToView(activeScene.models))
  }, [activeScene])

  useEffect(() => {
    const { isLoaded, status, scenes } = scenesQueryResult

    if (!isLoaded || status !== Status.FULFILLED || !scenes?.list?.length) {
      return
    }

    setActiveScene(scenes.list[0])
  }, [
    scenesQueryResult.isLoaded,
    scenesQueryResult.status,
    scenesQueryResult.scenes,
  ])

  useEffect(() => {
    const { isLoaded, status, levels, blockOrders } = buildingQueryResult

    if (!isLoaded || status !== Status.FULFILLED) {
      return
    }

    setLevels(levels)
    setBLocks(blockOrders)
  }, [buildingQueryResult.isLoaded, buildingQueryResult.status])

  const ContainerComponent =
    template === Template.V2 ? ContainerV2 : ContainerV1

  return (
    <ContainerComponent>
      <DataHandler
        payloads={[
          {
            name: 'scenesQueryResult',
            payload: {
              ...scenesQueryResult,
              data: scenesQueryResult.scenes,
            },
          },
          {
            name: 'buildingQueryResult',
            payload: {
              ...buildingQueryResult,
              data: buildingQueryResult.levels,
            },
          },
        ]}
        skeletonFrame={<ThreeDSkeleton template={template} />}
      >
        <div className="absolute left-5 top-5 z-8">
          <div className="flex items-center gap-4">
            {template === Template.Default && (
              <button
                type="button"
                className="rounded bg-white p-2 drop-shadow-40"
                onClick={() => handleOnFullScreen()}
              >
                <FullScreenSvg width="25" height="25" />
              </button>
            )}
            {activeModelData?.prev && (
              <button
                onClick={() => handleBackButton()}
                type="button"
                className="rounded bg-white p-1 drop-shadow-40"
              >
                <ArrowSvg className="h-8 w-8" strokeColor="#000" />
              </button>
            )}
            {activeModelData?.hasFiltering && (
              <button
                data-testid="toggle-filter"
                onClick={() => toggleFilter(!isFilterOpen)}
                type="button"
                className="rounded bg-white p-2.5 drop-shadow-40"
              >
                <FilterSvg className="h-5 w-5" />
              </button>
            )}
            {activeModelData?.hasFiltering && blockKeys.length > 1 ? (
              <ZoneDropdown
                activeZone={activeBlock}
                zoneOptions={blockOptions}
                onChange={onChangeZone}
              />
            ) : null}
          </div>
        </div>
        {canRenderModel && (
          <div className="relative h-full w-full overflow-hidden">
            <Model
              models={models}
              modelControl={modelControl}
              getModelStatus={setIsModelLoaded}
              getPosition={handleGetPosition}
              getCanAnimate={setCameraIsAnimating}
              detectChange={setDetectChange}
              ref={modelRef}
              enablePan={false}
              color="skyblue"
              getHighlightedTarget={(value) => {
                setHighlightedTarget(value)
                setHighlightedLevelInfo(value)
              }}
              interactionMode={!isDesktop ? 'point' : 'hover'}
              enablePerformanceStats={ENABLE_PERFORMANCE_STATS === 'true'}
            />
          </div>
        )}
        {unitFilter && template === Template.Default && session ? (
          <div className="">
            <FilterV1
              toggle={toggleFilter}
              isOpen={isFilterOpen}
              firebaseUnitFilter={unitFilter}
              firebaseActiveBlock={activeBlock}
              firebaseActiveLevel={session.building.activeLevel}
              hideLevel
            />
          </div>
        ) : null}
        {unitFilter && template === Template.V2 ? (
          <div
            className={`absolute left-0 top-0 z-9 h-full w-[297px] rounded-tr-2xl transition-transform duration-500 ${isFilterOpen ? 'translate-x-0' : '-translate-x-full'} `}
          >
            <FilterV2
              isOpen={isFilterOpen}
              building={{
                levels,
                blockOrders: blocks,
              }}
              firebaseUnitFilter={unitFilter}
              firebaseActiveBlock={activeBlock}
              toggle={toggleFilter}
              hideLevelOption
              filterStyle="top-0 z-10 h-full rounded-tr-2xl"
            />
          </div>
        ) : null}
        {highlightedLevel && (
          <div className="absolute right-8 top-8 z-1">
            <HighlightedLevelInfo
              highlightedLevel={highlightedLevel}
              propertyCount={propertyCount}
            />
          </div>
        )}
      </DataHandler>
    </ContainerComponent>
  )
}

export default connect(
  ({
    projectIdentity,
    firestore: { session },
    appConfig,
    blockLevel: { blocks },
  }: RootStateTypeExtra) => ({
    projectIdentity,
    showPrice: projectIdentity.showPrice,
    session,
    fullscreen: appConfig.fullscreen,
    storeBlocks: blocks,
  })
)(ADThreeDView)
